import React, { useState } from "react";
import { ExternalLink, Code, Layers, Star } from "lucide-react";

export default function Projects() {
  const [flippedCard, setFlippedCard] = useState(null);

  const works = [
    {
      id: 0,
      title: "Emporium Africa",
      describe:
        "Emporium is an e-commerce platform designed to facilitate purchase of a wide range of items, while also connecting businesses and consumers, streamlining the procurement process for bulk orders.",
      liveTo: "https://emporium.africa",
      category: "E-commerce",
      status: "Live",
      tools: ["Next.js", "TypeScript", "Redux", "MUI"],
      gradient: "from-blue-600 to-purple-600",
      image: "/images/projects/emporium-africa.png",
    },
    {
      id: 1,
      title: "RIBI Blood",
      describe:
        "RIBI Blood is focused on bridging the gap in getting volunteering blood donors to those in need of blood.",
      liveTo: "https://ribiblood.com/",
      category: "Healthcare",
      status: "Live",
      tools: ["HTML & CSS", "React.js", "Redux", "MUI"],
      gradient: "from-red-500 to-pink-500",
      image: "/images/projects/ribi-blood.png",
    },
    {
      id: 2,
      title: "Blog Site",
      describe:
        "A blog site clone, with a WYSIWYG rich text editor. Still in progress to add auth protection.",
      liveTo: "https://blog-next-gamma-lake.vercel.app/",
      category: "Content Management",
      status: "In Progress",
      tools: ["Next.js", "JavaScript", "MUI"],
      gradient: "from-green-500 to-teal-500",
      image: "/images/projects/blog-site.png",
    },
    {
      id: 3,
      title: "IMDb Clone",
      describe:
        "MovieBox is an intuitive web application that provides seamless navigation, stylish design, and accessible components, ensuring an engaging user experience. Integrated with the IMDB API to provide users with top rated films and trending movies.",
      liveTo: "https://next-imdb-clone.vercel.app/",
      category: "Entertainment",
      status: "Live",
      tools: ["React.js", "Next.js", "Tailwind CSS", "IMDB API"],
      gradient: "from-yellow-500 to-orange-500",
      image: "/images/projects/imdb-clone.png",
    },
    {
      id: 4,
      title: "Mortgage Calculator",
      describe:
        "A loan calculator for calculating principal amount, payable interest and total amount payable before taking a loan.",
      liveTo: "https://devguy-mortgage-calculator.netlify.app/",
      category: "Finance",
      status: "Live",
      tools: ["HTML & CSS", "JavaScript"],
      gradient: "from-indigo-500 to-purple-500",
      image: "/images/projects/mortgage-calculator.png",
    },
  ];

  const handleCardFlip = (id) => {
    setFlippedCard(flippedCard === id ? null : id);
  };

  return (
    <section id="works" className="min-h-screen">
      <div className="relative">
        <div className="border-b border-[#dfdfdf] pt-20 pb-14 px-6">
          <div className="sm:container mx-auto">
            <h1 className="text-[9vw] md:text-7xl text-text-[#DFDFDF] uppercase leading-5 font-extrabold">
              Featured Projects
            </h1>
          </div>
        </div>

        <div className="sm:container mx-auto p-5">
          {/* Projects Grid */}
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {works.map((project) => (
              <div
                key={project.id}
                className="group perspective-1000 h-96"
                onMouseEnter={() => handleCardFlip(project.id)}
                onMouseLeave={() => handleCardFlip(null)}
              >
                <div
                  className={`relative w-full h-full transition-transform duration-700 transform-style-preserve-3d ${
                    flippedCard === project.id ? "rotate-y-180" : ""
                  }`}
                >
                  {/* Front of Card */}
                  <div className="absolute inset-0 w-full h-full backface-hidden rounded-2xl overflow-hidden shadow-xl">
                    <div className={`relative h-full bg-[#e0f6ff1a] p-1`}>
                      <div className="bg-gray-900 h-full rounded-xl p-6 flex flex-col">
                        {/* Status Badge */}
                        <div className="flex justify-between items-start mb-4">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              project.status === "Live"
                                ? "bg-green-500/20 text-green-300"
                                : "bg-yellow-500/20 text-yellow-300"
                            }`}
                          >
                            {project.status}
                          </span>
                          <div className="text-gray-400">
                            <Layers size={20} />
                          </div>
                        </div>

                        {/* Project Image */}
                        <div className="relative mb-4 rounded-lg overflow-hidden flex-1">
                          <img
                            src={project.image}
                            alt={project.title}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
                        </div>

                        {/* Project Info */}
                        <div className="space-y-3">
                          <div>
                            <h3 className="text-xl font-bold text-white mb-1">
                              {project.title}
                            </h3>
                            <p className="text-sm text-gray-400">
                              {project.category}
                            </p>
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex space-x-2">
                              {project.tools.slice(0, 2).map((tool, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-gray-800 text-gray-300 text-xs rounded-md"
                                >
                                  {tool}
                                </span>
                              ))}
                              {project.tools.length > 2 && (
                                <span className="px-2 py-1 bg-gray-700 text-gray-400 text-xs rounded-md">
                                  +{project.tools.length - 2}
                                </span>
                              )}
                            </div>

                            <a
                              href={project.liveTo}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-white/10 rounded-lg text-white hover:bg-white/20 transition-colors duration-200"
                            >
                              <ExternalLink size={16} />
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Back of Card */}
                  <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180 rounded-2xl overflow-hidden shadow-xl">
                    <div
                      className={`relative h-full bg-gradient-to-br ${project.gradient} p-1`}
                    >
                      <div className="bg-gray-900 h-full rounded-xl p-6 flex flex-col">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="text-xl font-bold text-white">
                            {project.title}
                          </h3>
                          <div className="flex items-center space-x-2">
                            <Star className="text-yellow-400" size={16} />
                            <Code className="text-blue-400" size={16} />
                          </div>
                        </div>

                        {/* Description */}
                        <div className="flex-1 mb-6">
                          <p className="text-gray-300 text-sm leading-relaxed">
                            {project.describe}
                          </p>
                        </div>

                        {/* Tech Stack */}
                        <div className="space-y-4">
                          <h4 className="text-sm font-semibold text-gray-200">
                            Tech Stack
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {project.tools.map((tool, index) => (
                              <span
                                key={index}
                                className="px-3 py-1 bg-gray-800 text-gray-300 text-xs rounded-full border border-gray-700"
                              >
                                {tool}
                              </span>
                            ))}
                          </div>

                          {/* Actions */}
                          <div className="pt-4">
                            <a
                              href={project.liveTo}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full flex items-center justify-center space-x-2 bg-white text-gray-900 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
                            >
                              <ExternalLink size={16} />
                              <span>View Live Project</span>
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="text-center my-16">
            <div className="inline-flex items-center space-x-2 text-gray-400">
              <div className="w-8 h-0.5 bg-gradient-to-r from-transparent to-gray-500"></div>
              <span className="text-sm font-medium">
                More projects coming soon
              </span>
              <div className="w-8 h-0.5 bg-gradient-to-l from-transparent to-gray-500"></div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .perspective-1000 {
          perspective: 1000px;
        }
        .transform-style-preserve-3d {
          transform-style: preserve-3d;
        }
        .backface-hidden {
          backface-visibility: hidden;
        }
        .rotate-y-180 {
          transform: rotateY(180deg);
        }
      `}</style>
    </section>
  );
}
